import React from 'react';
import { motion } from 'framer-motion';
import {
  Link2,
  Mail,
  Users,
  ArrowRight,
  MessageSquare,
  Settings,
  TrendingUp,
  Activity,
  BarChart3,
  Database,
  DatabaseBackup,
  SlidersHorizontal,
  ShieldCheck,
  Server,
  Search,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { storage } from '../../../Shared/Utils/authStorage';
import { OVERVIEW_PATH } from './SupportTabNav';

/**
 * Business Support landing menu. A hub in the style of User Management: cards that branch into
 * the real tools rather than a dense dashboard. Generate Growth Link leads to the channel
 * overview where outreach starts; Email Template Config is a reserved entry with no screen
 * behind it yet. The relocated admin services (feedback, settings, growth, analytics, data)
 * are listed here too.
 */
function BusinessSupportMenu() {
  const navigate = useNavigate();
  const canManageRoles =
    storage.getRole() === 'SUPER_ADMIN' ||
    storage.hasAllPrivileges('ROLE_UPDATE', 'ASSIGN_ROLE');
  const canViewSystemHealth =
    storage.getRole() === 'SUPER_ADMIN' ||
    storage.hasPrivilege('SYSTEM_HEALTH_READ');
  const canManageSeo =
    storage.getRole() === 'SUPER_ADMIN' ||
    storage.hasPrivilege('SETTINGS_UPDATE');

  const cards = [
    {
      key: 'generate-link',
      label: 'Generate Growth Link',
      description: 'Build a tracked link and follow up with visitors per channel',
      icon: <Link2 size={18} />,
      accent: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400',
      path: OVERVIEW_PATH,
      disabled: false,
    },
    {
      key: 'candidates-data',
      label: 'Candidates Data',
      description: 'Search candidates and recruiters, share filtered lists and track engagement',
      icon: <Users size={18} />,
      accent: 'border-teal-500/30 bg-teal-500/10 text-teal-400',
      path: '/admin/support/candidates-data',
      disabled: false,
    },
    {
      key: 'take-backup',
      label: 'Take Backup',
      description: 'Export a database into its per-environment backup folder',
      icon: <DatabaseBackup size={18} />,
      accent: 'border-indigo-500/30 bg-indigo-500/10 text-indigo-400',
      path: '/admin/support/backup',
      disabled: false,
    },
    {
      key: 'system-health',
      label: 'System Health',
      description: 'Monitor VPS services, logs, incidents and performance',
      icon: <Server size={18} />,
      accent: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400',
      path: '/admin/support/system-health',
      disabled: false,
      hidden: !canViewSystemHealth,
    },
    {
      key: 'seo-setup',
      label: 'SEO Setup',
      description: 'Page titles, descriptions, sitemap and robots rules',
      icon: <Search size={18} />,
      accent: 'border-teal-500/30 bg-teal-500/10 text-teal-400',
      path: '/admin/support/seo-setup',
      disabled: false,
      hidden: !canManageSeo,
    },
    {
      key: 'role-assignment',
      label: 'Role Assignment',
      description: 'Create roles and assign privileges to admin users',
      icon: <ShieldCheck size={18} />,
      accent: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
      path: '/admin/support/role-assignment',
      hidden: !canManageRoles,
    },
    {
      key: 'email',
      label: 'Email',
      description: 'Configure templates and send outreach mail',
      icon: <Mail size={18} />,
      accent: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
      path: '/admin/support/email',
      disabled: false,
    },
    {
      key: 'feedback',
      label: 'Feedback',
      description: 'Review and respond to candidate and recruiter feedback',
      icon: <MessageSquare size={18} />,
      accent: 'border-violet-500/30 bg-violet-500/10 text-violet-400',
      path: '/admin/feedback',
      disabled: false,
    },
    {
      key: 'settings',
      label: 'Settings',
      description: 'Administration and platform configuration',
      icon: <Settings size={18} />,
      accent: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
      path: '/admin/settings',
      disabled: false,
    },
    {
      key: 'growth',
      label: 'Growth',
      description: 'Track acquisition, conversion and traffic sources',
      icon: <TrendingUp size={18} />,
      accent: 'border-orange-500/30 bg-orange-500/10 text-orange-400',
      path: '/admin/growth',
      disabled: false,
    },
    {
      key: 'system-analytics',
      label: 'System Analytics',
      description: 'Internal notes and the admin roster',
      icon: <Activity size={18} />,
      accent: 'border-pink-500/30 bg-pink-500/10 text-pink-400',
      path: '/admin/system-analytics',
      disabled: false,
    },
    {
      key: 'smart-analytics',
      label: 'Smart Analytics',
      description: 'Cross-section analytics for candidates, recruiters and admins',
      icon: <BarChart3 size={18} />,
      accent: 'border-sky-500/30 bg-sky-500/10 text-sky-400',
      path: '/admin/smart-analytics',
      disabled: false,
    },
    {
      key: 'data-service',
      label: 'Data Service',
      description: 'Query and manage platform data',
      icon: <Database size={18} />,
      accent: 'border-lime-500/30 bg-lime-500/10 text-lime-400',
      path: '/admin/data-service',
      disabled: false,
    },
    {
      key: 'system-configuration',
      label: 'System Configuration',
      description: 'Portal-wide email and password validation rules',
      icon: <SlidersHorizontal size={18} />,
      accent: 'border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-400',
      path: '/admin/settings/system-config',
      disabled: false,
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <div className="mb-8 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
          <Users size={22} />
        </span>
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">
            Business Support
          </h1>
          <p className="text-xs font-semibold text-slate-400 md:text-sm">
            Link generation and outreach configuration
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {cards.filter((card) => !card.hidden).map((card, idx) => {
          if (card.disabled) {
            return (
              <motion.button
                key={card.key}
                type="button"
                disabled
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.1 + idx * 0.05 }}
                className="group flex w-full cursor-not-allowed items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-900/40 p-4 text-left opacity-60"
              >
                <div className="flex items-center gap-3">
                  <span className={`flex h-10 w-10 items-center justify-center rounded-xl border ${card.accent}`}>
                    {card.icon}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-slate-100">{card.label}</p>
                    <p className="text-[11px] font-semibold text-slate-500">{card.description}</p>
                  </div>
                </div>
              </motion.button>
            );
          }

          return (
            <motion.button
              key={card.key}
              type="button"
              onClick={() => card.path && navigate(card.path)}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.35, delay: 0.1 + idx * 0.05 }}
              className="group flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-900/60 p-4 text-left transition hover:border-cyan-500/40 hover:bg-slate-900"
            >
              <div className="flex items-center gap-3">
                <span className={`flex h-10 w-10 items-center justify-center rounded-xl border ${card.accent}`}>
                  {card.icon}
                </span>
                <div>
                  <p className="text-sm font-bold text-slate-100">{card.label}</p>
                  <p className="text-[11px] font-semibold text-slate-500">{card.description}</p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 flex-shrink-0 text-slate-600 transition group-hover:translate-x-0.5 group-hover:text-cyan-400" />
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

export default BusinessSupportMenu;